import React, { useState, useEffect, useCallback } from 'react';
import nbrApi from '../../../services/nbrApi';
import moment from 'moment';
import Chart from 'react-apexcharts';
import FullPageLoader from '../../components/common/FullPageLoader';
import { toast } from 'react-toastify';
import '../../../css/multi-property-monitor.css';

const RANGE_OPTIONS = [
    { value: 'today', label: 'Today' },
    { value: 'this_week', label: 'This Week' },
    { value: 'this_month', label: 'This Month' }
];

const LAST_REFRESH_STORAGE_KEY = 'pvrLastRefreshMeta';

const getDefaultDateRange = () => {
    const todayStart = moment().startOf('day');
    return {
        from: todayStart.format('YYYY-MM-DD'),
        to: todayStart.format('YYYY-MM-DD')
    };
};

const SECONDS_IN_MINUTE = 60;

// Generate distinct colors for multiple series
// Using a more diverse color palette to ensure better distinction
const generateColors = (count) => {
    const colors = [
        '#48BAC8', // Aqua (Primary brand color)
        '#9C27B0', // Purple
        '#FF6B6B', // Red
        '#4ECDC4', // Teal
        '#45B7D1', // Blue
        '#FFA07A', // Light Salmon
        '#F7DC6F', // Yellow
        '#BB8FCE', // Light Purple
        '#F8B739', // Orange
        '#52BE80', // Green
        '#EC7063', // Coral
        '#5DADE2', // Light Blue
        '#F1948A', // Pink
        '#82E0AA', // Light Green
        '#D35400', // Dark Orange
        '#8E44AD', // Dark Purple
        '#16A085', // Dark Teal
        '#E74C3C', // Dark Red
        '#3498DB', // Bright Blue
        '#2ECC71', // Bright Green
        '#F39C12', // Gold
        '#E67E22', // Carrot
        '#1ABC9C', // Turquoise
        '#34495E', // Dark Gray
    ];
    
    // If we need more colors than available, generate variations
    const result = [];
    for (let i = 0; i < count; i++) {
        if (i < colors.length) {
            result.push(colors[i]);
        } else {
            // Generate a color by rotating hue for additional resources
            const baseColor = colors[i % colors.length];
            // Create a slight variation by adjusting brightness
            result.push(baseColor);
        }
    }
    return result;
};

function PerformanceVariancesReport() {
    const selectedLoginData = JSON.parse(localStorage.getItem('selectedLoginData')) || {};
    const propertiesId = selectedLoginData?.properties_id;
    const defaultRange = getDefaultDateRange();

    // State management
    const [fromDate, setFromDate] = useState(defaultRange.from);
    const [toDate, setToDate] = useState(defaultRange.to);
    const [activeRange, setActiveRange] = useState('today');
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(false);
    const [lastRefreshTime, setLastRefreshTime] = useState(null);
    const [lastRefreshFilters, setLastRefreshFilters] = useState(null);
    const [yAxisMax, setYAxisMax] = useState(null); // null means auto-scale
    const [yAxisMin, setYAxisMin] = useState(null); // null means auto-scale
    const [chartData, setChartData] = useState({
        series: [],
        options: {
            chart: {
                type: 'line',
                height: 450,
                zoom: {
                    enabled: false
                },
                toolbar: {
                    show: false
                }
            },
            title: {
                // text: 'Performance Variances Report',
                align: 'center',
                style: {
                    fontSize: '20px',
                    color: '#333',
                    fontWeight: 'bold'
                }
            },
            xaxis: {
                type: 'datetime',
                title: {
                    text: 'Date/Time',
                    style: {
                        color: '#333'
                    }
                },
                labels: {
                    style: {
                        colors: '#333',
                        fontSize: '10px'
                    },
                    datetimeUTC: false
                }
            },
            yaxis: {
                title: {
                    text: 'Cumulative Variance (Minutes)',
                    style: {
                        color: '#333'
                    }
                },
                labels: {
                    style: {
                        colors: '#333'
                    },
                    formatter: function (val) {
                        return val.toFixed(2);
                    }
                },
                // Allow negative values (no min restriction)
                // Zero line will be shown via grid configuration
                min: undefined, // Will be set dynamically
                max: undefined, // Will be set dynamically
            },
            stroke: {
                curve: 'smooth',
                width: 2.5,
                lineCap: 'round'
            },
            tooltip: {
                enabled: true,
                theme: 'dark',
                shared: true,
                followCursor: true,
                intersect: false,
                fixed: {
                    enabled: false
                },
                x: {
                    format: 'MMM dd, yyyy HH:mm'
                },
                y: {
                    formatter: function (val, { seriesIndex, w }) {
                        const sign = val >= 0 ? '+' : '';
                        return `${sign}${val.toFixed(2)} minutes`;
                    }
                },
                marker: {
                    show: true
                }
            },
            legend: {
                position: 'top',
                horizontalAlign: 'right',
                labels: {
                    colors: '#333',
                    useSeriesColors: false
                },
                showForSingleSeries: true,
                onItemClick: {
                    toggleDataSeries: true
                },
                onItemHover: {
                    highlightDataSeries: true
                }
            },
            grid: {
                borderColor: '#e7e7e7',
                xaxis: {
                    lines: {
                        show: true
                    }
                },
                yaxis: {
                    lines: {
                        show: true
                    }
                },
                // Add a zero line to clearly show positive vs negative variances
                padding: {
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0
                }
            },
            // Add a zero line marker
            annotations: {
                yaxis: [
                    {
                        y: 0,
                        borderColor: '#999',
                        borderWidth: 2,
                        borderDashArray: [5, 5],
                        strokeDashArray: 5,
                        opacity: 0.8,
                        label: {
                            // text: 'Zero Baseline',
                            style: {
                                color: '#666',
                                background: '#fff',
                                fontSize: '11px',
                                fontWeight: 600,
                                padding: {
                                    left: 6,
                                    right: 6,
                                    top: 3,
                                    bottom: 3
                                },
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                            },
                            position: 'right',
                            offsetX: 10
                        }
                    }
                ]
            },
            dataLabels: {
                enabled: false
            },
            markers: {
                size: 0,
                hover: {
                    size: 5,
                    sizeOffset: 2
                },
                discrete: []
            }
        }
    });

    // Apply date range presets
    const applyDateRange = (rangeKey) => {
        switch (rangeKey) {
            case 'today': {
                const todayStart = moment().startOf('day');
                setFromDate(todayStart.format('YYYY-MM-DD'));
                setToDate(todayStart.format('YYYY-MM-DD'));
                setActiveRange(rangeKey);
                break;
            }
            case 'this_week': {
                const startOfWeek = moment().startOf('isoWeek');
                setFromDate(startOfWeek.format('YYYY-MM-DD'));
                setToDate(startOfWeek.clone().endOf('isoWeek').format('YYYY-MM-DD'));
                setActiveRange(rangeKey);
                break;
            }
            case 'this_month': {
                const startOfMonth = moment().startOf('month');
                setFromDate(startOfMonth.format('YYYY-MM-DD'));
                setToDate(startOfMonth.clone().endOf('month').format('YYYY-MM-DD'));
                setActiveRange(rangeKey);
                break;
            }
            default:
                break;
        }
    };

    // Fetch resources list for display names
    const fetchResources = async () => {
        if (!propertiesId) {
            return;
        }

        try {
            const response = await nbrApi.post('/gethkresources', {
                properties_id: propertiesId
            });

            if (response?.data && Array.isArray(response.data)) {
                setResources(response.data);
            } else {
                setResources([]);
            }
        } catch (error) {
            console.error('Error fetching resources:', error);
            setResources([]);
        }
    };

    const storeRefreshMetadata = useCallback((timestamp) => {
        const meta = {
            timestamp,
            filters: {
                fromDate,
                toDate,
                rangePreset: activeRange
            }
        };
        setLastRefreshTime(timestamp);
        setLastRefreshFilters(meta.filters);
        try {
            localStorage.setItem(LAST_REFRESH_STORAGE_KEY, JSON.stringify(meta));
        } catch (error) {
            console.error('Unable to store refresh metadata', error);
        }
    }, [fromDate, toDate, activeRange]);

    // Fetch cumulative variances data
    const fetchReportData = useCallback(async () => {
        if (!propertiesId) {
            toast.error('Property ID not found. Please log in again.');
            return;
        }

        setLoading(true);
        try {
            const response = await nbrApi.post('/getcumulativevariances', {
                properties_id: propertiesId,
                time_start: fromDate,
                time_stop: toDate
            });

            if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
                // Group data by resources_id and collect resource names
                const dataByResource = {};
                const resourceNamesMap = {}; // Store resource names from API response
                
                response.data.forEach(item => {
                    const resourceId = item.resources_id;
                    if (!resourceId) return;
                    
                    // Store resource name from API response (more reliable)
                    if (item.resource_name && !resourceNamesMap[resourceId]) {
                        resourceNamesMap[resourceId] = item.resource_name;
                    }
                    
                    // Only process items that have cuml_net_seconds and time_stop
                    if (item.cuml_net_seconds === undefined || item.cuml_net_seconds === null || !item.time_stop) {
                        return;
                    }
                    
                    if (!dataByResource[resourceId]) {
                        dataByResource[resourceId] = [];
                    }
                    
                    dataByResource[resourceId].push({
                        timestamp: moment(item.time_stop).valueOf(),
                        cuml_net_seconds: item.cuml_net_seconds
                    });
                });

                // Sort each resource's data by timestamp
                Object.keys(dataByResource).forEach(resourceId => {
                    dataByResource[resourceId].sort((a, b) => a.timestamp - b.timestamp);
                });

                // Find the maximum timestamp across all resources (latest time point)
                let maxTimestamp = 0;
                Object.keys(dataByResource).forEach(resourceId => {
                    const resourceData = dataByResource[resourceId];
                    if (resourceData.length > 0) {
                        const lastTimestamp = resourceData[resourceData.length - 1].timestamp;
                        if (lastTimestamp > maxTimestamp) {
                            maxTimestamp = lastTimestamp;
                        }
                    }
                });

                // Create series for each resource
                const series = [];
                const resourceIds = Object.keys(dataByResource).sort((a, b) => parseInt(a) - parseInt(b));
                const colors = generateColors(resourceIds.length);

                resourceIds.forEach((resourceId, index) => {
                    const resourceData = dataByResource[resourceId];
                    
                    // Use resource_name from API response, fallback to resources list, then to ID
                    let resourceName = resourceNamesMap[resourceId];
                    if (!resourceName) {
                        const resourceInfo = resources.find(
                            r => (r.resources_id || r.id) === parseInt(resourceId)
                        );
                        resourceName = resourceInfo 
                            ? (resourceInfo.resource_name || resourceInfo.name || `Resource ${resourceId}`)
                            : `Resource ${resourceId}`;
                    }

                    // Map data points
                    const seriesData = resourceData.map(item => ({
                        x: item.timestamp,
                        y: item.cuml_net_seconds / SECONDS_IN_MINUTE // Convert seconds to minutes
                    }));

                    // Extend line to the maximum timestamp if this resource's last point is earlier
                    // This ensures all lines extend to the same end time for comparison
                    if (resourceData.length > 0 && maxTimestamp > 0) {
                        const lastPoint = resourceData[resourceData.length - 1];
                        if (lastPoint.timestamp < maxTimestamp) {
                            // Add a point at the max timestamp with the same Y value as the last point
                            // This extends the line horizontally to show the cumulative variance continues
                            seriesData.push({
                                x: maxTimestamp,
                                y: lastPoint.cuml_net_seconds / SECONDS_IN_MINUTE
                            });
                        }
                    }

                    series.push({
                        name: resourceName,
                        data: seriesData,
                        color: colors[index]
                    });
                });

                // Calculate Y-axis range from data if not manually set
                // Make it symmetric around zero
                let calculatedMin = null;
                let calculatedMax = null;
                
                if (series.length > 0) {
                    let allValues = [];
                    series.forEach(s => {
                        s.data.forEach(point => {
                            if (point.y !== null && point.y !== undefined) {
                                allValues.push(point.y);
                            }
                        });
                    });
                    
                    if (allValues.length > 0) {
                        const dataMin = Math.min(...allValues);
                        const dataMax = Math.max(...allValues);
                        // Find the maximum absolute value to make symmetric around zero
                        const maxAbsValue = Math.max(Math.abs(dataMin), Math.abs(dataMax));
                        // Add some padding (10%)
                        const padding = Math.max(maxAbsValue * 0.1, 5);
                        // Make symmetric around zero
                        calculatedMax = maxAbsValue + padding;
                        calculatedMin = -(maxAbsValue + padding);
                    }
                }

                // Update chart data
                setChartData(prev => ({
                    ...prev,
                    series: series,
                    options: {
                        ...prev.options,
                        colors: colors,
                        yaxis: {
                            ...prev.options.yaxis,
                            min: yAxisMin !== null ? yAxisMin : calculatedMin,
                            max: yAxisMax !== null ? yAxisMax : calculatedMax
                        }
                    }
                }));
                
                // Reset Y-axis limits when new data is loaded (optional - comment out if you want to preserve user settings)
                // setYAxisMax(null);
                // setYAxisMin(null);

                toast.success(`Report loaded successfully with ${resourceIds.length} resource(s) and ${response.data.length} data points`);
                storeRefreshMetadata(moment().toISOString());
            } else {
                setChartData(prev => ({
                    ...prev,
                    series: []
                }));
                toast.info('No data found for the selected date range');
                storeRefreshMetadata(moment().toISOString());
            }
        } catch (error) {
            console.error('Error fetching report data:', error);
            toast.error('Failed to fetch report data. Please try again.');
            setChartData(prev => ({
                ...prev,
                series: []
            }));
            storeRefreshMetadata(moment().toISOString());
        } finally {
            setLoading(false);
        }
    }, [propertiesId, fromDate, toDate, resources, storeRefreshMetadata, yAxisMin, yAxisMax]);

    useEffect(() => {
        try {
            const storedMeta = localStorage.getItem(LAST_REFRESH_STORAGE_KEY);
            if (storedMeta) {
                const parsed = JSON.parse(storedMeta);
                if (parsed?.timestamp) {
                    setLastRefreshTime(parsed.timestamp);
                }
                if (parsed?.filters) {
                    setLastRefreshFilters(parsed.filters);
                    if (parsed.filters.fromDate) {
                        setFromDate(parsed.filters.fromDate);
                    }
                    if (parsed.filters.toDate) {
                        setToDate(parsed.filters.toDate);
                    }
                    if (parsed.filters.rangePreset) {
                        setActiveRange(parsed.filters.rangePreset);
                    }
                }
            }
        } catch (error) {
            console.error('Unable to read stored refresh metadata', error);
        }
    }, []);

    // Initialize resources
    useEffect(() => {
        fetchResources();
    }, [propertiesId]);

    // Update chart Y-axis when limits change
    useEffect(() => {
        if (chartData.series.length > 0) {
            setChartData(prev => ({
                ...prev,
                options: {
                    ...prev.options,
                    yaxis: {
                        ...prev.options.yaxis,
                        min: yAxisMin,
                        max: yAxisMax
                    }
                }
            }));
        }
    }, [yAxisMin, yAxisMax]);

    // Auto-fetch data when dates change
    useEffect(() => {
        if (propertiesId) {
            fetchReportData();
        }
    }, [fromDate, toDate, propertiesId, fetchReportData]);

    return (
        <div className="container-fluid">
            {/* Header */}
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <h3 className="mb-0 text-primary fw-bold text-s">
                                <i className="bi bi-graph-up me-2"></i>
                                Performance Variances Report
                            </h3>
                            <p className="text-muted mb-0 mt-2">
                                View cumulative variances across all housekeeping resources. Each line represents a different resource's variance from zero over time.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Controls */}
            <div className="row mb-2">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <div className="row align-items-end g-3">
                                {/* From Date */}
                                <div className="col-12 col-md-6 col-lg-3">
                                    <label htmlFor="fromDate" className="form-label fw-bold text-primary">
                                        <i className="bi bi-calendar-event me-2"></i>
                                        From Date
                                    </label>
                                    <input
                                        type="date"
                                        id="fromDate"
                                        className="form-control"
                                        value={fromDate}
                                        onChange={(e) => {
                                            setFromDate(e.target.value);
                                            if (activeRange !== 'custom') {
                                                setActiveRange('custom');
                                            }
                                        }}
                                    />
                                </div>

                                {/* To Date */}
                                <div className="col-12 col-md-6 col-lg-3">
                                    <label htmlFor="toDate" className="form-label fw-bold text-primary">
                                        <i className="bi bi-calendar-check me-2"></i>
                                        To Date
                                    </label>
                                    <input
                                        type="date"
                                        id="toDate"
                                        className="form-control"
                                        value={toDate}
                                        onChange={(e) => {
                                            setToDate(e.target.value);
                                            if (activeRange !== 'custom') {
                                                setActiveRange('custom');
                                            }
                                        }}
                                    />
                                </div>

                                {/* Quick Ranges */}
                                <div className="col-12 col-md-6 col-lg-4">
                                    <label className="form-label fw-bold text-primary d-block">
                                        <i className="bi bi-lightning-charge me-2"></i>
                                        Quick Ranges
                                    </label>
                                    <div className="d-flex flex-wrap gap-2">
                                        {RANGE_OPTIONS.map(option => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                className={`btn btn-sm ${activeRange === option.value ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => applyDateRange(option.value)}
                                                style={{ flex: '1 1 auto', minWidth: '100px' }}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Generate Button */}
                                <div className="col-12 col-md-6 col-lg-2">
                                    <label className="form-label fw-bold text-primary d-block d-lg-none">
                                        &nbsp;
                                    </label>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-primary w-100"
                                        onClick={fetchReportData}
                                        disabled={loading}
                                    >
                                        <i className="bi bi-search me-2"></i>
                                        {loading ? 'Loading...' : 'Generate'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Chart */}
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm border-0" style={{ position: 'relative' }}>
                        <div className="card-body" style={{ position: 'relative', minHeight: '500px' }}>
                            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                                <div className="text-muted small">
                                    Last refreshed:{' '}
                                    {lastRefreshTime
                                        ? moment(lastRefreshTime).format('MMM DD, YYYY HH:mm:ss')
                                        : 'Not yet refreshed'}
                                    {lastRefreshFilters && (
                                        <>
                                            <span className="mx-2">|</span>
                                            <span>
                                                Date Range: {moment(lastRefreshFilters.fromDate).format('MMM DD, YYYY')} → {moment(lastRefreshFilters.toDate).format('MMM DD, YYYY')}
                                            </span>
                                        </>
                                    )}
                                </div>
                                {chartData.series.length > 0 && (() => {
                                    // Calculate current range from data
                                    let allValues = [];
                                    chartData.series.forEach(s => {
                                        s.data.forEach(point => {
                                            if (point.y !== null && point.y !== undefined) {
                                                allValues.push(point.y);
                                            }
                                        });
                                    });
                                    
                                    // Calculate symmetric range around zero
                                    let dataMin = allValues.length > 0 ? Math.min(...allValues) : -100;
                                    let dataMax = allValues.length > 0 ? Math.max(...allValues) : 100;
                                    const maxAbsValue = Math.max(Math.abs(dataMin), Math.abs(dataMax));
                                    const padding = Math.max(maxAbsValue * 0.1, 5);
                                    const symmetricMax = maxAbsValue + padding;
                                    const symmetricMin = -(maxAbsValue + padding);
                                    
                                    // Use current manual limits or calculated symmetric range
                                    const currentMax = yAxisMax !== null ? yAxisMax : symmetricMax;
                                    const currentMin = yAxisMin !== null ? yAxisMin : symmetricMin;
                                    
                                    return (
                                        <div className="d-flex align-items-center gap-2">
                                            <label className="text-muted small mb-0 me-2">
                                                <i className="bi bi-arrows-vertical me-1"></i>
                                                Viewable Variance Range:
                                            </label>
                                            <div className="btn-group btn-group-sm" role="group">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary"
                                                    onClick={() => {
                                                        // Zoom in: reduce range symmetrically around zero
                                                        const maxAbs = Math.max(Math.abs(currentMax), Math.abs(currentMin));
                                                        const newMaxAbs = maxAbs * 0.8; // Reduce by 20%
                                                        setYAxisMax(newMaxAbs);
                                                        setYAxisMin(-newMaxAbs);
                                                    }}
                                                    title="Zoom in (reduce viewable variance range)"
                                                >
                                                    <i className="bi bi-zoom-in"></i>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary"
                                                    onClick={() => {
                                                        // Zoom out: increase range symmetrically around zero
                                                        const maxAbs = Math.max(Math.abs(currentMax), Math.abs(currentMin));
                                                        const newMaxAbs = maxAbs * 1.25; // Increase by 25%
                                                        setYAxisMax(newMaxAbs);
                                                        setYAxisMin(-newMaxAbs);
                                                    }}
                                                    title="Zoom out (increase viewable variance range)"
                                                >
                                                    <i className="bi bi-zoom-out"></i>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary"
                                                    onClick={() => {
                                                        setYAxisMax(null);
                                                        setYAxisMin(null);
                                                    }}
                                                    title="Reset to auto-scale"
                                                >
                                                    <i className="bi bi-arrow-counterclockwise"></i> Reset
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                            {loading && (
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    zIndex: 10,
                                    borderRadius: '0.375rem'
                                }}>
                                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-3 text-muted">Loading, please wait...</p>
                                </div>
                            )}
                            {chartData.series.length > 0 ? (
                                <div style={{ width: '100%', overflow: 'hidden' }}>
                                    <Chart
                                        options={chartData.options}
                                        series={chartData.series}
                                        type="line"
                                        height={450}
                                    />
                                </div>
                            ) : (
                                !loading && (
                                    <div className="text-center py-5">
                                        <i className="bi bi-graph-up" style={{ fontSize: '48px', color: '#ccc' }}></i>
                                        <p className="text-muted mt-3">
                                            No data available for the selected date range
                                        </p>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PerformanceVariancesReport;

